import { Fetch } from '../utils'
import { ApiEndpoints } from '../constants'
import type { GlobalErrorHandler, Tag, Tagging, Transaction, TransactionRule } from '..'

export class TransactionApi {
  fetchInstance: Fetch

  constructor(endpoint: string, token: string, onError?: GlobalErrorHandler) {
    this.fetchInstance = new Fetch(endpoint, token, undefined, onError)
  }

  addTag = async (tag: Tag) => {
    return this.fetchInstance.post(ApiEndpoints.TAGS, tag).then((data) => data.tag)
  }

  addTagging = async (tagging: Tagging) => {
    return this.fetchInstance.post(ApiEndpoints.TAGGINGS, tagging).then((data) => data.tagging)
  }

  addTransaction = async (transaction: Transaction) => {
    return this.fetchInstance
      .post(ApiEndpoints.TRANSACTIONS, transaction)
      .then((data) => data.transaction)
  }

  addTransactionRule = async (rule: TransactionRule) => {
    return this.fetchInstance.post(ApiEndpoints.TRANSACTION_RULES, rule).then((data) => data)
  }

  getTaggings = async () => {
    return this.fetchInstance
      .get(ApiEndpoints.TAGGINGS)
      .then((data) => data.map((item: { tagging: Tagging }) => item.tagging))
  }

  getTags = async () => {
    return this.fetchInstance
      .get(ApiEndpoints.TAGS)
      .then((data) => data.map((item: { tag: Tag }) => item.tag))
  }

  getTransactionRules = async () => {
    return this.fetchInstance
      .get(ApiEndpoints.TRANSACTION_RULES)
      .then((data) => data.transaction_rules)
  }

  getTransactionsByDateRange = async (startDate: number, endDate: number) => {
    return this.fetchInstance
      .get(`${ApiEndpoints.TRANSACTIONS}/by_date/${startDate}/${endDate}`)
      .then((data) => data.map((item: { transaction: Transaction }) => item.transaction))
  }

  // DEPRECATED
  getTransactionsByAccountsAndDateRange = async (
    accountGuids: string[],
    beginDate: number,
    endDate: number,
  ) => {
    console.warn('DEPRECATED: getTransactionsByAccountsAndDateRange will be removed')
    const accountsList = accountGuids.reduce(
      (acc: string, guid: string, i: number) =>
        `${acc}account_guids[]=${guid}${i !== accountGuids.length - 1 ? '&' : ''}`,
      '',
    )

    return this.fetchInstance
      .get(`${ApiEndpoints.TRANSACTIONS}/by_date/${beginDate}/${endDate}?${accountsList}`)
      .then((data) => data.map((item: { transaction: Transaction }) => item.transaction))
  }

  // DEPRECATED
  getTransactionsByGuids = async (guids: string[]) => {
    console.warn('DEPRECATED: getTransactionsByGuids will be removed')
    return this.fetchInstance
      .post(`${ApiEndpoints.TRANSACTIONS}/by_guids`, { guids: guids.join('+') })
      .then((data) => data.map((item: { transaction: Transaction }) => item.transaction))
  }

  // DEPRECATED
  getTransactionsByCategoryAndDate = async (
    categoryGuid: string,
    beginDate: number,
    endDate: number,
  ) => {
    console.warn('DEPRECATED: getTransactionsByCategoryAndDate will be removed')
    return this.fetchInstance
      .get(
        `${ApiEndpoints.TRANSACTIONS}/by_category_guid/${categoryGuid}/by_date/${beginDate}/${endDate}`,
      )
      .then((data) => data.map((item: { transaction: Transaction }) => item.transaction))
  }

  // DEPRECATED
  getTransactionsByCategoryDateAndAccounts = async (
    categoryGuid: string,
    beginDate: number,
    endDate: number,
    accountGuids: string[],
  ) => {
    console.warn('DEPRECATED: getTransactionsByCategoryDateAndAccounts will be removed')
    const accountsList = accountGuids.reduce(
      (acc: string, guid: string, i: number) =>
        `${acc}account_guids[]=${guid}${i !== accountGuids.length - 1 ? '&' : ''}`,
      '',
    )

    return this.fetchInstance
      .get(
        `${ApiEndpoints.TRANSACTIONS}/by_category_guid/${categoryGuid}/by_date/${beginDate}/${endDate}?${accountsList}`,
      )
      .then((data) => data.map((item: { transaction: Transaction }) => item.transaction))
  }

  // DEPRECATED
  getTransactionsByCategoriesDateAndAccounts = async (
    categoryGuids: string[],
    beginDate: number,
    endDate: number,
    accountGuids: string[],
  ) => {
    console.warn('DEPRECATED: getTransactionsByCategoriesDateAndAccounts will be removed')
    const accountsList = accountGuids.reduce(
      (acc: string, guid: string, i: number) =>
        `${acc}account_guids[]=${guid}${i !== accountGuids.length - 1 ? '&' : ''}`,
      '',
    )

    return this.fetchInstance
      .get(
        `${ApiEndpoints.TRANSACTIONS}/by_category_guids/${categoryGuids.join(
          '+',
        )}/by_date/${beginDate}/${endDate}?${accountsList}`,
      )
      .then((data) => data.map((item: { transaction: Transaction }) => item.transaction))
  }

  /**
   * DEPRECATED
   * Get all transactions by the top level category, between begin date and end date, and optionally
   * filtered by accounts
   * @param categoryGuid The top level category guid
   * @param beginDate the start date
   * @param endDate the end date
   * @param accountGuids an optional list of account guids
   */
  getTransactionsByTopLevelCategoryAndDate = async (
    categoryGuid: string,
    beginDate: number,
    endDate: number,
    accountGuids: string[] = [],
  ) => {
    console.warn('DEPRECATED: getTransactionsByTopLevelCategoryAndDate will be removed')
    let url = `${ApiEndpoints.TRANSACTIONS}/by_top_level_category_guid/${categoryGuid}/by_date/${beginDate}/${endDate}`

    if (accountGuids.length > 0) {
      const accountsList = accountGuids.reduce(
        (acc: string, guid: string, i: number) =>
          `${acc}account_guids[]=${guid}${i !== accountGuids.length - 1 ? '&' : ''}`,
        '',
      )
      url += `?${accountsList}`
    }

    return this.fetchInstance
      .get(url)
      .then((data) => data.map((item: { transaction: Transaction }) => item.transaction))
  }

  removeTag = async (guid: string) => {
    return this.fetchInstance.delete(`${ApiEndpoints.TAGS}/${guid}`).then((data) => data)
  }

  removeTagging = async (guid: string) => {
    return this.fetchInstance.delete(`${ApiEndpoints.TAGGINGS}/${guid}`).then((data) => data)
  }

  removeTransaction = async (guid: string) => {
    return this.fetchInstance.delete(`${ApiEndpoints.TRANSACTIONS}/${guid}`).then((data) => data)
  }

  removeTransactionRule = async (guid: string) => {
    return this.fetchInstance.delete(`${ApiEndpoints.TRANSACTION_RULES}/${guid}`)
  }

  removeTransactionSplit = async (guid: string) => {
    return this.fetchInstance
      .delete(`${ApiEndpoints.TRANSACTIONS}/${guid}/unsplit`)
      .then((data) => data)
  }

  updateTransactionRule = async (rule: TransactionRule) => {
    return this.fetchInstance
      .put(`${ApiEndpoints.TRANSACTION_RULES}/${rule.guid}`, rule)
      .then((data) => data)
  }

  updateTag = async (tag: Tag) => {
    return this.fetchInstance.put(`${ApiEndpoints.TAGS}/${tag.guid}`, tag).then((data) => data.tag)
  }

  updateTransaction = async (transaction: Transaction) => {
    return this.fetchInstance
      .put(`${ApiEndpoints.TRANSACTIONS}/${transaction.guid}`, transaction)
      .then((data) => data.transaction)
  }
}
